import React, { createContext, useContext, useCallback, useState } from 'react';
import styles from '../styles/components/ConfirmDialog.module.css';

interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type ConfirmFn = (opts: string | ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

interface DialogState {
  options: ConfirmOptions;
  resolve: (v: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const confirm = useCallback((opts: string | ConfirmOptions): Promise<boolean> =>
    new Promise(resolve => {
      const options = typeof opts === 'string' ? { message: opts } : opts;
      setDialog({ options, resolve });
    }), []);

  const close = (result: boolean) => {
    dialog?.resolve(result);
    setDialog(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className={styles.overlay} onClick={() => close(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            {dialog.options.title && <h3 className={styles.title}>{dialog.options.title}</h3>}
            <p className={styles.message}>{dialog.options.message}</p>
            <div className={styles.actions}>
              <button className={styles.btnCancel} onClick={() => close(false)}>
                {dialog.options.cancelLabel ?? 'İptal'}
              </button>
              <button className={styles.btnConfirm} onClick={() => close(true)}>
                {dialog.options.confirmLabel ?? 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
